import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Shield, 
  Clock, 
  Star, 
  ChevronRight,
  Sparkles,
  Syringe,
  Stethoscope,
  HeartPulse,
  Activity
} from "lucide-react";

const highlights = [
  {
    icon: Shield,
    title: "Board-Certified Anesthesiologists",
    description: "Our team of fellowship-trained anesthesiologists brings decades of experience to every procedure, ensuring the highest standards of patient safety.",
  },
  {
    icon: Sparkles,
    title: "State-of-the-Art Facility",
    description: "Fully accredited outpatient surgery center equipped with the latest monitoring technology and luxury recovery suites.",
  },
  {
    icon: Clock,
    title: "Personalized Care",
    description: "From pre-operative consultation to post-procedure recovery, we tailor every aspect of your surgical experience to your needs.",
  },
];

const services = [
  { icon: Syringe, name: "General Anesthesia", description: "Complete sedation for major surgical procedures" },
  { icon: Activity, name: "IV Sedation", description: "Conscious sedation for minimally invasive procedures" },
  { icon: Stethoscope, name: "Regional Anesthesia", description: "Targeted nerve blocks and epidurals" },
  { icon: HeartPulse, name: "Pain Management", description: "Post-operative and chronic pain solutions" },
];

const procedures = [
  "Orthopedic Surgery",
  "Plastic & Cosmetic Surgery", 
  "Ophthalmology",
  "ENT Procedures",
  "Gastroenterology",
  "Podiatry",
  "Gynecology",
  "Dental Surgery"
];

export default function Home() {
  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=1920&h=1080&fit=crop')`,
          }}
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 hero-overlay" />
        
        {/* Radial Glow */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(112, 0, 255, 0.15) 0%, rgba(5, 5, 5, 0) 60%)'
          }}
        />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <div className="space-y-8">
            {/* Tagline */}
            <p className="text-neon-cyan uppercase tracking-[0.3em] text-sm md:text-base font-body animate-fade-in-up">
              Premier Outpatient Surgery Center
            </p>

            {/* Main Heading */}
            <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-none font-bold italic text-white animate-fade-in-up animation-delay-100">
              Expert <span className="gradient-text">Anesthesia</span>
              <br />
              Exceptional Care
            </h1>

            {/* Subheading */}
            <p className="text-gray-300 text-base md:text-xl max-w-2xl mx-auto leading-relaxed font-body animate-fade-in-up animation-delay-200">
              Surgery Center LLC provides comprehensive surgical care services 
              across our Atlanta and Newark locations. Board-certified physicians dedicated to your safety and comfort.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-up animation-delay-300">
              <Link to="/appointments">
                <Button
                  data-testid="hero-book-btn"
                  className="shimmer-btn rounded-full bg-white text-black hover:bg-neon-cyan hover:text-black font-bold uppercase tracking-widest px-8 md:px-10 py-5 md:py-6 text-sm md:text-base shadow-[0_0_30px_rgba(255,255,255,0.4)] hover:shadow-[0_0_40px_rgba(0,240,255,0.7)] transition-all duration-300"
                >
                  Schedule Consultation
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/services">
                <Button
                  data-testid="hero-services-btn"
                  variant="outline"
                  className="rounded-full border-2 border-white/30 bg-transparent text-white hover:border-neon-pink hover:text-neon-pink font-bold uppercase tracking-widest px-8 md:px-10 py-5 md:py-6 text-sm md:text-base transition-all duration-300"
                >
                  Our Services
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1.5 h-1.5 bg-neon-cyan rounded-full animate-glow-pulse" />
          </div>
        </div>
      </section>

      {/* Highlights Section */}
      <section data-testid="highlights-section" className="py-24 md:py-32 bg-vip-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium text-white">
              Why Choose <span className="text-neon-pink">Us</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {highlights.map((item, index) => (
              <div
                key={item.title}
                data-testid={`highlight-card-${index}`}
                className="vip-card glass rounded-2xl p-8 md:p-10 border border-white/10 hover:border-neon-cyan/30"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="service-icon w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                  <item.icon className="w-8 h-8 text-neon-cyan" />
                </div>
                <h3 className="font-heading text-xl md:text-2xl text-white mb-4">{item.title}</h3>
                <p className="text-gray-400 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Anesthesia Services */}
      <section data-testid="services-teaser" className="py-24 md:py-32 bg-vip-paper">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6">
            <div>
              <p className="text-neon-cyan uppercase tracking-[0.2em] text-sm mb-4">Anesthesia Services</p>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium text-white">
                Comprehensive Care
              </h2>
            </div>
            <Link to="/services">
              <Button
                data-testid="view-all-services-btn"
                variant="outline"
                className="rounded-full border border-white/20 bg-transparent text-white hover:border-neon-cyan hover:text-neon-cyan uppercase tracking-widest px-8 py-4 transition-all duration-300"
              >
                View All Services
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div
                key={service.name}
                data-testid={`service-card-${index}`}
                className="vip-card glass rounded-2xl p-6 border border-white/10 hover:border-neon-pink/30 group cursor-pointer"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-neon-cyan/20 to-neon-pink/20 flex items-center justify-center group-hover:neon-cyan-glow transition-all duration-300">
                    <service.icon className="w-6 h-6 text-neon-cyan" />
                  </div>
                  <h3 className="font-heading text-lg md:text-xl text-white">{service.name}</h3>
                </div>
                <p className="text-gray-400 text-sm">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Procedures We Support */}
      <section data-testid="procedures-section" className="py-24 md:py-32 bg-vip-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-neon-pink uppercase tracking-[0.2em] text-sm mb-4">Surgical Specialties</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium text-white">
              Procedures We Support
            </h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
              Our anesthesiologists provide expert care for a wide range of outpatient surgical procedures
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {procedures.map((procedure, index) => (
              <div
                key={procedure}
                data-testid={`procedure-${index}`}
                className="glass rounded-xl p-6 border border-white/10 hover:border-neon-cyan/30 text-center transition-all duration-300"
              >
                <p className="text-white font-medium">{procedure}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section data-testid="trust-section" className="py-24 md:py-32 bg-vip-paper overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "15+", label: "Years Experience" },
              { value: "10K+", label: "Procedures Performed" },
              { value: "100%", label: "Safety Record" },
              { value: "5-Star", label: "Patient Rating" },
            ].map((stat, index) => (
              <div key={index}>
                <p className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold gradient-text mb-2">
                  {stat.value}
                </p>
                <p className="text-gray-400 text-sm uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 md:py-32 relative overflow-hidden bg-vip-black">
        {/* Background */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(191, 64, 191, 0.2) 0%, rgba(5, 5, 5, 0) 70%)'
          }}
        />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-bold italic text-white mb-6">
            Your Safety Is Our <span className="gradient-text">Priority</span>
          </h2>
          <p className="text-gray-300 text-base md:text-lg mb-10 max-w-2xl mx-auto">
            Schedule a pre-operative consultation with our board-certified anesthesiologists 
            to discuss your upcoming procedure and anesthesia options.
          </p>
          <Link to="/appointments">
            <Button
              data-testid="cta-book-btn"
              className="shimmer-btn rounded-full bg-gradient-to-r from-neon-cyan via-royal-purple to-neon-pink text-white font-bold uppercase tracking-widest px-10 md:px-12 py-5 md:py-6 text-sm md:text-base shadow-[0_0_40px_rgba(191,64,191,0.4)] hover:shadow-[0_0_60px_rgba(191,64,191,0.6)] transition-all duration-300"
            >
              Book Consultation
              <Sparkles className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
